import React, { memo, useMemo } from 'react';
import { Dropdown } from 'antd';
import styled, { css } from 'styled-components';
import { ColorPaletteItem } from '@lgg/isomorphic';
import { Contact } from '@lgg/isomorphic/types/__generated__/graphql';
import { useContactAddNewOptions } from 'src/components/domain/contacts/contact-add-new/contact-add-new';
import { IconButtonV2 } from 'src/components/general/button/icon-button';
import { LinkButton } from 'src/components/general/button/lgg-button';
import { AddNewContextMenu } from 'src/components/general/display/add-new-context-menu';
import { Icon } from 'src/components/general/icon';
import { OpenLegacyCallModalIcon } from 'src/components/pages/conversations/components/open-legacy-call-modal-icon';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useUrls } from 'src/hooks/use-urls';

const sharedStyling = css`
  height: 30px;
  background: transparent;

  &,
  &:hover,
  &:active,
  &:focus {
    box-shadow: none;
  }
`;

const LinkIconButton = styled(LinkButton)<{
  iconSize?: number;
  $iconColor?: ColorPaletteItem;
}>`
  ${sharedStyling}
  svg {
    width: ${({ iconSize }) => (iconSize ? iconSize : 18)}px;
    height: ${({ iconSize }) => (iconSize ? iconSize : 18)}px;
    path {
      fill: ${({ $iconColor, theme }) =>
        $iconColor ? theme?.colors[$iconColor] : theme?.colors.monk};
    }
  }

  &,
  &:not([disabled]):hover {
    svg path {
      fill: ${({ $iconColor, theme }) =>
        $iconColor ? theme?.colors[$iconColor] : theme?.colors.monk};
    }
  }
`;

const BottomOptionIconButton = styled(IconButtonV2)<{
  iconColor?: ColorPaletteItem;
  iconSize?: number;
}>`
  ${sharedStyling}
  svg {
    width: ${({ iconSize }) => (iconSize ? iconSize : 18)}px;
    height: ${({ iconSize }) => (iconSize ? iconSize : 18)}px;
    path {
      fill: ${({ iconColor, theme }) =>
        iconColor ? theme?.colors[iconColor] : theme?.colors.monk};
    }
  }

  &,
  &:not([disabled]):hover {
    svg path {
      fill: ${({ iconColor, theme }) =>
        iconColor ? theme?.colors[iconColor] : theme?.colors.monk};
    }
  }
`;

type ContactActionIconsProps = {
  contact: Contact;
  iconColorOverride?: ColorPaletteItem;
};

export const ContactActionIcons = memo<ContactActionIconsProps>(
  ({ contact, iconColorOverride }) => {
    const { getContactModalUrl } = useUrls();
    const { getFeatureFlag } = useAuthorization();
    const hasCrmFeatureFlag = getFeatureFlag('crm');
    const contactAddNewOptions = useContactAddNewOptions({ contactId: contact.id });

    const callContactButton = useMemo(
      () => (
        <OpenLegacyCallModalIcon
          contact={contact}
          data-lgg-id="contact-preview-call-contact"
        />
      ),
      [contact],
    );

    return (
      <>
        {callContactButton}
        <LinkIconButton
          data-lgg-id="contact-preview-show-conversation"
          variant="defaultWhite"
          $iconColor={iconColorOverride}
          to={getContactModalUrl({ contactId: contact.id, tab: 'conversations' })}
        >
          <Icon type="conversations" />
        </LinkIconButton>
        {hasCrmFeatureFlag && (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            getPopupContainer={(n) => n}
            overlay={
              <AddNewContextMenu
                groups={[
                  (contactAddNewOptions ?? [])?.map((option) => {
                    const testId = option['data-lgg-id'] ?? '';

                    return {
                      ...option,
                      testId,
                      key: testId,
                    };
                  }),
                ]}
              />
            }
          >
            <span>
              <BottomOptionIconButton
                data-lgg-id="contact-preview-contact-actions"
                icon="contactActions"
                size="small"
                variant="defaultWhite"
                iconColor={iconColorOverride ?? 'cosmo'}
              />
            </span>
          </Dropdown>
        )}
      </>
    );
  },
);
